import { useMemo, Fragment } from "react";
import { Chart, useChart } from "@chakra-ui/charts";
import { TfiLayoutLineSolid, TfiLineDashed } from "react-icons/tfi";
import { Box, Card, Flex, HStack, Icon, IconButton, Stack, Text } from "@chakra-ui/react";
import { Tooltip as ChakraTooltip } from "@/components/ui/tooltip";
import { CartesianGrid, Line, LineChart, Tooltip, TooltipContentProps, XAxis, YAxis } from "recharts";

import { IObservationStats } from "@/types/data";
import { IInterval } from "./timespan";
import { MdInfoOutline } from "react-icons/md";

interface ILineChart {
  title: string;
  info?: string;
  interval?: IInterval;
  data: IObservationStats[];
  outplantingDate?: string;
}

function LineChartBox({ title, info, data, outplantingDate, interval = "week" }: ILineChart) {
  const formattedData = useMemo(() => {
    // Convert area to percentage, but set null for zero values to allow "connectNulls" to work properly - except on the first item.
    const mapped = data.map((item, i) => ({
      ...item,
      average_area:
      i === 0
        ? item.average_area * 100
        : item.average_area === 0
          ? null
          : item.average_area * 100,
      predicted_area: (i === data.length - 1 && outplantingDate) ? item.average_area * 100 : null,
    }));
    if (outplantingDate) {
      mapped.push({
        date: outplantingDate,
        predicted_area: 75,
        // fake data to fit the IObservationStats type:
        average_area: null,
        fragments_count: 0,
        aptasia: 0,
        algae: 0,
        paleness: 0,
        mucus: 0,
        bleaching: 0,
        tissue_loss: 0,
        dead_fragments: 0,
        parasites: 0,
        predation: 0,
      });
    }
    return mapped;
  }, [data, outplantingDate]);

  const dataSeries = [
    { name: "average_area" as const, label: "Observed", color: "blue.500" },
    ...(outplantingDate
      ? [{ name: "predicted_area" as const, label: "Predicted", color: "blue.500", strokeDasharray: "5 5" }]
      : []),
  ];

  const chart = useChart(
    {
      data: formattedData,
      series: dataSeries,
    }
  );

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const dateOptions: Record<string, Intl.DateTimeFormatOptions> = {
      day: { day: "2-digit", month: "short" },
      week: { day: "2-digit", month: "short" },
      month: { month: "short", year: "numeric"},
    };
    const options = dateOptions[interval];
    // The API returns the dates in the UTC timezone
    return new Intl.DateTimeFormat("en-US", {...options, timeZone: "UTC"}).format(date);
  };

  return (
    <Card.Root size="sm">
      <Card.Body>
        <Flex justifyContent="space-between" alignItems="center" mb="6">
          <Text textAlign="left" fontWeight="600">{ title }</Text>
          {info?.length && (
            <ChakraTooltip content={info} contentProps={{ color: "white" }}>
              <IconButton variant="ghost" size="xs" opacity="0.5">
                <MdInfoOutline />
              </IconButton>
            </ChakraTooltip>)}
        </Flex>
        <Box>
          <Chart.Root ml="-4" chart={chart} height="13.25rem">
            <LineChart data={chart.data} responsive>
              <CartesianGrid stroke={chart.color("border")} vertical={false} />
              <XAxis
                axisLine={false}
                tickMargin={10}
                dataKey={chart.key("date")}
                tickFormatter={(date: string) => formatDate(date)}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tickMargin={10}
                domain={[0, "dataMax + 10"]}
                tickFormatter={(value) => String(Math.ceil(value))}
                unit="%"
              />
              <Tooltip
                animationDuration={100}
                cursor={false}
                content={CustomTooltip}
              />
              {chart.series.map((item) => (
                <Line
                  key={item.name}
                  isAnimationActive={false}
                  dataKey={chart.key(item.name)}
                  stroke={chart.color(item.color)}
                  strokeWidth={3}
                  strokeDasharray={item.strokeDasharray}
                  type="monotone"
                  dot={false}
                  connectNulls
                />
              ))}
            </LineChart>
          </Chart.Root>
        </Box>
        <Flex gap="2" alignItems="center" maxW="380px" marginInline="auto" mt="4" ml="4">
          {dataSeries.map((item) => (
            <Fragment key={item.name}>
              {item.strokeDasharray
                ? <Icon size="lg" color={item.color}><TfiLineDashed /></Icon>
                : <Icon size="lg" color={item.color}><TfiLayoutLineSolid /></Icon>}
              <Text fontSize="xs">{item.label}</Text>
            </Fragment>
          ))}
        </Flex>
      </Card.Body>
    </Card.Root>
  );
}

function CustomTooltip(props: TooltipContentProps) {
  const { active, payload, label } = props;
  if (!active || !payload || payload.length === 0) return null;
  // as we are plotting a line for the past observations and another one for the predicted area growth,
  // we should filter out duplicate values in the tootip to make it more comprehensible
  const filteredPayload = payload.filter((i, index) => {
    if (index === 0) return true;
    if (i.value === payload[index - 1].value) return false;
    return true;
  });

  return (
    <Box rounded="sm" bg="bg" p="3" borderWidth="1px" borderColor="border">
      <HStack>
        <span>{label}</span>
      </HStack>
      <Stack>
        {filteredPayload.map((item) => (
          <HStack key={item.name}>
            <Box boxSize="2" bg={item.color} borderRadius={100} />
            <Text fontSize="lg">{Number(item.value).toFixed(2).replace(/\.00$/, "")}%</Text>
          </HStack>
        ))}
      </Stack>
    </Box>
  );
}

export default LineChartBox;
